// src/utils/dilemmaScoring.js
// Scoring + clustering for the 20-question, 4-option "What Would You Do?" quiz.
// Each option contributes 7 pts to a primary value dimension and 3 pts to a secondary.
//
// Balance philosophy:
//   - 20 questions × 4 options = 80 primary slots + 80 secondary slots.
//   - Each of the 8 value dimensions appears ~10× as primary and ~10× as
//     secondary. Small (±1–2) imbalances are absorbed by centroid clustering.
//
// Cluster determination:
//   - Each of the 6 worldview types has an 8-D centroid (its "ideal" score
//     profile). We use COSINE SIMILARITY (direction match) rather than
//     Euclidean distance, plus a std-dev-based override that routes flat
//     score profiles to The Bridge. See determineClusterFromScores below.

const DILEMMA_SCORING_MATRIX = {
  // ---- Original questions (Q1–Q10) ----
  q1: {
    a: { CARE_EMPATHY: 7, TRADITION_LOYALTY: 3 },
    b: { SECURITY_AUTHORITY: 7, ACHIEVEMENT_PRAGMATISM: 3 },
    c: { ACHIEVEMENT_PRAGMATISM: 7, AUTONOMY_LIBERTY: 3 },
    d: { FAIRNESS_EQUITY: 7, PROGRESS_OPENNESS: 3 },
  },
  q2: {
    a: { FAIRNESS_EQUITY: 7, SECURITY_AUTHORITY: 3 },
    b: { CARE_EMPATHY: 7, TRADITION_LOYALTY: 3 },
    c: { AUTONOMY_LIBERTY: 7, SANCTITY_PURITY: 3 },
    d: { PROGRESS_OPENNESS: 7, FAIRNESS_EQUITY: 3 },
  },
  q3: {
    a: { TRADITION_LOYALTY: 7, SECURITY_AUTHORITY: 3 },
    b: { FAIRNESS_EQUITY: 7, CARE_EMPATHY: 3 },
    c: { ACHIEVEMENT_PRAGMATISM: 7, PROGRESS_OPENNESS: 3 },
    d: { CARE_EMPATHY: 7, AUTONOMY_LIBERTY: 3 },
  },
  q4: {
    a: { SECURITY_AUTHORITY: 7, TRADITION_LOYALTY: 3 },
    b: { FAIRNESS_EQUITY: 7, PROGRESS_OPENNESS: 3 },
    c: { SANCTITY_PURITY: 7, AUTONOMY_LIBERTY: 3 },
    d: { ACHIEVEMENT_PRAGMATISM: 7, CARE_EMPATHY: 3 },
  },
  q5: {
    a: { TRADITION_LOYALTY: 7, CARE_EMPATHY: 3 },
    b: { SANCTITY_PURITY: 7, FAIRNESS_EQUITY: 3 },
    c: { AUTONOMY_LIBERTY: 7, TRADITION_LOYALTY: 3 },
    d: { PROGRESS_OPENNESS: 7, AUTONOMY_LIBERTY: 3 },
  },
  q6: {
    a: { ACHIEVEMENT_PRAGMATISM: 7, SECURITY_AUTHORITY: 3 },
    b: { SANCTITY_PURITY: 7, CARE_EMPATHY: 3 },
    c: { PROGRESS_OPENNESS: 7, ACHIEVEMENT_PRAGMATISM: 3 },
    d: { TRADITION_LOYALTY: 7, SECURITY_AUTHORITY: 3 },
  },
  q7: {
    a: { FAIRNESS_EQUITY: 7, SECURITY_AUTHORITY: 3 },
    b: { ACHIEVEMENT_PRAGMATISM: 7, SANCTITY_PURITY: 3 },
    c: { AUTONOMY_LIBERTY: 7, PROGRESS_OPENNESS: 3 },
    d: { SECURITY_AUTHORITY: 7, TRADITION_LOYALTY: 3 },
  },
  q8: {
    a: { SECURITY_AUTHORITY: 7, ACHIEVEMENT_PRAGMATISM: 3 },
    b: { CARE_EMPATHY: 7, FAIRNESS_EQUITY: 3 },
    c: { TRADITION_LOYALTY: 7, SECURITY_AUTHORITY: 3 },
    d: { PROGRESS_OPENNESS: 7, FAIRNESS_EQUITY: 3 },
  },
  q9: {
    a: { PROGRESS_OPENNESS: 7, CARE_EMPATHY: 3 },
    b: { SECURITY_AUTHORITY: 7, TRADITION_LOYALTY: 3 },
    c: { ACHIEVEMENT_PRAGMATISM: 7, SECURITY_AUTHORITY: 3 },
    d: { AUTONOMY_LIBERTY: 7, CARE_EMPATHY: 3 },
  },
  q10: {
    a: { FAIRNESS_EQUITY: 7, ACHIEVEMENT_PRAGMATISM: 3 },
    b: { CARE_EMPATHY: 7, SANCTITY_PURITY: 3 },
    c: { SANCTITY_PURITY: 7, FAIRNESS_EQUITY: 3 },
    d: { TRADITION_LOYALTY: 7, PROGRESS_OPENNESS: 3 },
  },

  // ---- New questions (Q11–Q20) — designed to balance dimensions ----
  q11: {
    // Lie under oath for best friend
    a: { SANCTITY_PURITY: 7, TRADITION_LOYALTY: 3 }, // won't perjure
    b: { TRADITION_LOYALTY: 7, CARE_EMPATHY: 3 }, // loyalty isn't on/off
    c: { CARE_EMPATHY: 7, AUTONOMY_LIBERTY: 3 }, // it depends
    d: { ACHIEVEMENT_PRAGMATISM: 7, FAIRNESS_EQUITY: 3 }, // build a real defense
  },
  q12: {
    // Boss falsifying expenses, anonymous tip
    a: { FAIRNESS_EQUITY: 7, SANCTITY_PURITY: 3 }, // drop the tip
    b: { PROGRESS_OPENNESS: 7, CARE_EMPATHY: 3 }, // confront directly
    c: { AUTONOMY_LIBERTY: 7, SECURITY_AUTHORITY: 3 }, // not my circus
    d: { ACHIEVEMENT_PRAGMATISM: 7, FAIRNESS_EQUITY: 3 }, // document quietly
  },
  q13: {
    // Inheritance from estranged parent, sibling cared for them
    a: { FAIRNESS_EQUITY: 7, CARE_EMPATHY: 3 }, // split equally
    b: { AUTONOMY_LIBERTY: 7, SANCTITY_PURITY: 3 }, // keep — their choice
    c: { SECURITY_AUTHORITY: 7, ACHIEVEMENT_PRAGMATISM: 3 }, // courts sort it
    d: { SANCTITY_PURITY: 7, PROGRESS_OPENNESS: 3 }, // donate it all
  },
  q14: {
    // AI cover letter landed dream job
    a: { SANCTITY_PURITY: 7, FAIRNESS_EQUITY: 3 }, // tell them
    b: { AUTONOMY_LIBERTY: 7, PROGRESS_OPENNESS: 3 }, // everyone uses AI
    c: { ACHIEVEMENT_PRAGMATISM: 7, SANCTITY_PURITY: 3 }, // outwork the doubt
    d: { PROGRESS_OPENNESS: 7, AUTONOMY_LIBERTY: 3 }, // use AI for the job too
  },
  q15: {
    // Dating profile small lie
    a: { SANCTITY_PURITY: 7, CARE_EMPATHY: 3 }, // bring it up
    b: { AUTONOMY_LIBERTY: 7, PROGRESS_OPENNESS: 3 }, // let it go
    c: { SECURITY_AUTHORITY: 7, SANCTITY_PURITY: 3 }, // I'm out
    d: { CARE_EMPATHY: 7, AUTONOMY_LIBERTY: 3 }, // we connect in person
  },
  q16: {
    // Group chat trash-talking absent friend
    a: { FAIRNESS_EQUITY: 7, SANCTITY_PURITY: 3 }, // speak up
    b: { SECURITY_AUTHORITY: 7, ACHIEVEMENT_PRAGMATISM: 3 }, // stay quiet
    c: { TRADITION_LOYALTY: 7, CARE_EMPATHY: 3 }, // change subject
    d: { SANCTITY_PURITY: 7, FAIRNESS_EQUITY: 3 }, // tell them later
  },
  q17: {
    // Friend's bad business idea, $5k
    a: { CARE_EMPATHY: 7, TRADITION_LOYALTY: 3 }, // give them money
    b: { SANCTITY_PURITY: 7, ACHIEVEMENT_PRAGMATISM: 3 }, // tell them honestly
    c: { SECURITY_AUTHORITY: 7, AUTONOMY_LIBERTY: 3 }, // decline politely
    d: { ACHIEVEMENT_PRAGMATISM: 7, CARE_EMPATHY: 3 }, // refine the plan
  },
  q18: {
    // Charity collector, exaggerated story
    a: { CARE_EMPATHY: 7, PROGRESS_OPENNESS: 3 }, // donate
    b: { ACHIEVEMENT_PRAGMATISM: 7, AUTONOMY_LIBERTY: 3 }, // vet first
    c: { AUTONOMY_LIBERTY: 7, SECURITY_AUTHORITY: 3 }, // never give door-to-door
    d: { FAIRNESS_EQUITY: 7, ACHIEVEMENT_PRAGMATISM: 3 }, // research first
  },
  q19: {
    // Witness shoplifting baby formula
    a: { AUTONOMY_LIBERTY: 7, CARE_EMPATHY: 3 }, // didn't see
    b: { SECURITY_AUTHORITY: 7, TRADITION_LOYALTY: 3 }, // tell manager
    c: { CARE_EMPATHY: 7, SANCTITY_PURITY: 3 }, // pay myself
    d: { TRADITION_LOYALTY: 7, FAIRNESS_EQUITY: 3 }, // letting it slide isn't compassion
  },
  q20: {
    // Old roommate wants to crash indefinitely
    a: { TRADITION_LOYALTY: 7, CARE_EMPATHY: 3 }, // yes of course
    b: { ACHIEVEMENT_PRAGMATISM: 7, FAIRNESS_EQUITY: 3 }, // two weeks max
    c: { CARE_EMPATHY: 7, ACHIEVEMENT_PRAGMATISM: 3 }, // help find another place
    d: { SANCTITY_PURITY: 7, AUTONOMY_LIBERTY: 3 }, // my space is the routine
  },
};

// Auto-derive max scores per dimension from the matrix.
const DILEMMA_MAX_SCORES = (() => {
  const dims = [
    'SECURITY_AUTHORITY',
    'TRADITION_LOYALTY',
    'CARE_EMPATHY',
    'FAIRNESS_EQUITY',
    'AUTONOMY_LIBERTY',
    'PROGRESS_OPENNESS',
    'SANCTITY_PURITY',
    'ACHIEVEMENT_PRAGMATISM',
  ];
  const maxes = {};
  dims.forEach(d => (maxes[d] = 0));

  for (const questionScores of Object.values(DILEMMA_SCORING_MATRIX)) {
    const questionMax = {};
    dims.forEach(d => (questionMax[d] = 0));
    for (const optionScores of Object.values(questionScores)) {
      for (const [dim, pts] of Object.entries(optionScores)) {
        questionMax[dim] = Math.max(questionMax[dim], pts);
      }
    }
    for (const dim of dims) maxes[dim] += questionMax[dim];
  }
  return maxes;
})();

/**
 * Calculate 8-dimension scores from dilemma quiz answers.
 * @param {Object} answers - { q1: 'a', q2: 'c', ... }
 * @returns {Object} 8 dimension keys, each 0-100.
 */
export function calculateDilemmaScores(answers) {
  const rawScores = {
    SECURITY_AUTHORITY: 0,
    TRADITION_LOYALTY: 0,
    CARE_EMPATHY: 0,
    FAIRNESS_EQUITY: 0,
    AUTONOMY_LIBERTY: 0,
    PROGRESS_OPENNESS: 0,
    SANCTITY_PURITY: 0,
    ACHIEVEMENT_PRAGMATISM: 0,
  };

  for (const [questionId, optionKey] of Object.entries(answers)) {
    const questionMatrix = DILEMMA_SCORING_MATRIX[questionId];
    if (!questionMatrix) continue;
    const optionScores = questionMatrix[optionKey];
    if (!optionScores) continue;
    for (const [dimension, points] of Object.entries(optionScores)) {
      rawScores[dimension] += points;
    }
  }

  const normalizedScores = {};
  for (const [dimension, rawScore] of Object.entries(rawScores)) {
    const maxScore = DILEMMA_MAX_SCORES[dimension] || 1;
    normalizedScores[dimension] = Math.round((rawScore / maxScore) * 100);
  }
  return normalizedScores;
}

// ----------------------------------------------------------------------
// Cluster determination — direction-based (cosine similarity) with a
// variance-based override for genuinely Balanced users.
// ----------------------------------------------------------------------
//
// Why cosine instead of Euclidean: random users score ~28% on every
// dimension. Euclidean distance to a 50-50-50 "Balanced" centroid is
// always shorter than to a directional one (e.g. CARE 80, FAIRNESS 80),
// so 60%+ of random users would land in Balanced. Cosine similarity
// compares the DIRECTION of a user's score vector rather than its
// magnitude — perfect for "what do they care most about" clustering.
//
// Balanced is special: a genuinely flat scorer has no clear direction,
// so cosine is unreliable for them. We detect them by score VARIANCE
// (low std-dev = Balanced) and route them directly to The Bridge.

// Centroids encode the DIRECTION each type leans, with values in the
// 0-100 range mirroring user score space. Magnitude differences within
// a centroid emphasize what defines that type.
const CLUSTER_CENTROIDS = [
  {
    id: 0,
    name: 'Equity Champions', // The Compass — care + fairness
    centroid: {
      SECURITY_AUTHORITY: 25,
      TRADITION_LOYALTY: 25,
      CARE_EMPATHY: 90,
      FAIRNESS_EQUITY: 95,
      AUTONOMY_LIBERTY: 50,
      PROGRESS_OPENNESS: 60,
      SANCTITY_PURITY: 35,
      ACHIEVEMENT_PRAGMATISM: 40,
    },
  },
  {
    id: 1,
    name: 'Progressive Catalysts', // The Torch — autonomy + progress
    centroid: {
      SECURITY_AUTHORITY: 20,
      TRADITION_LOYALTY: 15,
      CARE_EMPATHY: 50,
      FAIRNESS_EQUITY: 55,
      AUTONOMY_LIBERTY: 90,
      PROGRESS_OPENNESS: 95,
      SANCTITY_PURITY: 25,
      ACHIEVEMENT_PRAGMATISM: 50,
    },
  },
  {
    id: 2,
    name: 'Expressive Innovators', // The Mirror — care + sanctity
    centroid: {
      SECURITY_AUTHORITY: 25,
      TRADITION_LOYALTY: 30,
      CARE_EMPATHY: 90,
      FAIRNESS_EQUITY: 45,
      AUTONOMY_LIBERTY: 55,
      PROGRESS_OPENNESS: 45,
      SANCTITY_PURITY: 90,
      ACHIEVEMENT_PRAGMATISM: 25,
    },
  },
  {
    id: 3,
    name: 'Balanced Nonconformists', // The Bridge — flat profile
    centroid: {
      SECURITY_AUTHORITY: 50,
      TRADITION_LOYALTY: 50,
      CARE_EMPATHY: 50,
      FAIRNESS_EQUITY: 50,
      AUTONOMY_LIBERTY: 50,
      PROGRESS_OPENNESS: 50,
      SANCTITY_PURITY: 50,
      ACHIEVEMENT_PRAGMATISM: 50,
    },
  },
  {
    id: 4,
    name: 'Pragmatic Visionaries', // The Architect — achievement + security
    centroid: {
      SECURITY_AUTHORITY: 65,
      TRADITION_LOYALTY: 35,
      CARE_EMPATHY: 30,
      FAIRNESS_EQUITY: 45,
      AUTONOMY_LIBERTY: 40,
      PROGRESS_OPENNESS: 55,
      SANCTITY_PURITY: 25,
      ACHIEVEMENT_PRAGMATISM: 95,
    },
  },
  {
    id: 5,
    name: 'Traditional Guardians', // The Anchor — tradition + security
    centroid: {
      SECURITY_AUTHORITY: 90,
      TRADITION_LOYALTY: 95,
      CARE_EMPATHY: 45,
      FAIRNESS_EQUITY: 45,
      AUTONOMY_LIBERTY: 25,
      PROGRESS_OPENNESS: 20,
      SANCTITY_PURITY: 60,
      ACHIEVEMENT_PRAGMATISM: 40,
    },
  },
];

const DIM_KEYS = [
  'SECURITY_AUTHORITY',
  'TRADITION_LOYALTY',
  'CARE_EMPATHY',
  'FAIRNESS_EQUITY',
  'AUTONOMY_LIBERTY',
  'PROGRESS_OPENNESS',
  'SANCTITY_PURITY',
  'ACHIEVEMENT_PRAGMATISM',
];

function vec(scores) {
  return DIM_KEYS.map(k => scores[k] || 0);
}

function magnitude(v) {
  let sumSq = 0;
  for (const x of v) sumSq += x * x;
  return Math.sqrt(sumSq);
}

/**
 * Cosine similarity between two score vectors. Returns 1 when perfectly
 * aligned, 0 when orthogonal, -1 when opposite.
 */
function cosineSimilarity(a, b) {
  const va = vec(a);
  const vb = vec(b);
  let dot = 0;
  for (let i = 0; i < va.length; i++) dot += va[i] * vb[i];
  const denom = magnitude(va) * magnitude(vb);
  return denom === 0 ? 0 : dot / denom;
}

/**
 * Standard deviation of the user's 8 dimension scores. Low std-dev means
 * the user is genuinely balanced (no strong directional preference).
 */
function scoreStdDev(scores) {
  const v = vec(scores);
  const mean = v.reduce((a, b) => a + b, 0) / v.length;
  const variance = v.reduce((acc, x) => acc + (x - mean) ** 2, 0) / v.length;
  return Math.sqrt(variance);
}

// Threshold: anyone with std-dev below this is treated as Balanced.
// Tuned empirically — see scripts/simulate-clusters.mjs.
const BALANCED_STDDEV_THRESHOLD = 7.5;

/**
 * Determine which of the 6 clusters a user belongs to.
 *   1. If score std-dev is low → Balanced Nonconformists (no clear direction).
 *   2. Otherwise pick the centroid with the highest cosine similarity.
 *
 * @param {Object} scores - 8-dim scores (0-100 each)
 * @returns {{ cluster: number, name: string }}
 */
export function determineClusterFromScores(scores) {
  if (scoreStdDev(scores) < BALANCED_STDDEV_THRESHOLD) {
    const balanced = CLUSTER_CENTROIDS.find(c => c.id === 3);
    return { cluster: balanced.id, name: balanced.name };
  }

  let best = CLUSTER_CENTROIDS[0];
  let bestSim = -Infinity;
  for (const c of CLUSTER_CENTROIDS) {
    if (c.id === 3) continue; // Balanced is handled by the std-dev override
    const sim = cosineSimilarity(scores, c.centroid);
    if (sim > bestSim) {
      bestSim = sim;
      best = c;
    }
  }
  return { cluster: best.id, name: best.name };
}

export {
  DILEMMA_SCORING_MATRIX,
  DILEMMA_MAX_SCORES,
  CLUSTER_CENTROIDS,
  cosineSimilarity,
  scoreStdDev,
};
